import React, { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { ThemeProvider, createTheme, alpha } from "@mui/material/styles";
import { useIdleTimer } from "react-idle-timer";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";

//imoprt Route
import { fireSafetyRoles } from "common/constant";
import { layoutModeTypes } from "Components/constants/layout";
import { ChatBot } from "pages/Dashboard/ChatBot";
import { fireSafetyHomeRoute, homeRoute } from "Routes/constants";
import Route from "Routes";

import decodeToken from "utils/decodeToken";
import { deviceToken, isAuthorizedUser } from "utils";

import { loginSuccess, logoutUserSuccess } from "slices/auth/login/reducer";
import { changeLayoutMode } from "slices/thunks";
import { api } from "slices/api";
import { useLazyGetUserQuery } from "apis/User";

//import Scss
import "assets/scss/themes.scss";
import colors from "assets/scss/colors.module.scss";

const getPalette = (mode) => {
  return mode === "light"
    ? {
        mode,
        primary: {
          main: colors.blue,
          disable: alpha(colors.blue, 0.3),
        },
        drawer: {
          main: colors.blue,
        },
      }
    : {
        mode,
        primary: {
          main: colors.lightBlue,
          disable: alpha(colors.lightBlue, 0.3),
        },
        error: {
          main: colors.lightRed,
        },
        drawer: {
          main: "#272727",
        },
      };
};

function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const mode = useSelector((state) => state.Layout.layoutModeType);
  const { isLoggedIn, roles } = useSelector((state) => state.Login);
  const [getUserData, { data: { data: userData } = {} }] =
    useLazyGetUserQuery();
  const token = localStorage.getItem("giftCityToken");

  const deviceTokenID = localStorage.getItem("deviceToken");
  if (!deviceTokenID) {
    const systemId = deviceToken();
    localStorage.setItem("deviceToken", systemId);
  }

  const fetchUserData = async (role, userId) => {
    try {
      const roles = Array.isArray(role) ? role : [role];
      const { data: userData } = await getUserData(
        userId,
        {},
        isAuthorizedUser(roles, fireSafetyRoles)
      );
      dispatch(loginSuccess({ user: userData, roles }));
    } catch (err) {}
  };

  const onIdle = () => {
    if (isLoggedIn) {
      navigate(
        window.location.href.includes("fire-safety")
          ? fireSafetyHomeRoute
          : homeRoute
      );
      dispatch(logoutUserSuccess());
      dispatch(changeLayoutMode(layoutModeTypes["LIGHTMODE"]));
      dispatch(api.util.resetApiState());
    }
  };

  useIdleTimer({
    onIdle,
    timeout: 600000,
  });

  useEffect(() => {
    if (token) {
      const { role, userId } = decodeToken(token);
      fetchUserData(role, userId);
    }
  }, []);

  useEffect(() => {
    if (userData) {
      dispatch(loginSuccess({ user: userData, roles }));
    }
  }, [userData]);

  const theme = useMemo(
    () =>
      createTheme({
        palette: getPalette(mode),
      }),
    [mode]
  );

  return (
    <React.Fragment>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <ThemeProvider theme={theme}>
          <Route />
          {!isLoggedIn && <ChatBot />}
        </ThemeProvider>
      </LocalizationProvider>
      <ToastContainer autoClose={2000} limit={5} />
    </React.Fragment>
  );
}

export default App;
